// Rate limiting queue for cart attribute updates
const cartAttributeQueue = [];
let isProcessingCartAttribute = false;
const currentDelay = 500;
const BATCH_WINDOW = 250; // Wait to collect multiple requests
let batchTimeout = null;

const getCookieValue = (name) => {
  const cookieString = `; ${document.cookie}`;
  const parts = cookieString.split(`; ${name}=`);
  return parts.length === 2 ? parts.pop().split(';').shift() : '';
};

const setCookieValue = (name, value, expires) => {
  const date = new Date();
  date.setTime(date.getTime() + expires * 24 * 60 * 60 * 1000);
  const expiresString = `; expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value}${expiresString}; path=/`;
};

const processCartAttributeQueue = async () => {
  if (isProcessingCartAttribute || cartAttributeQueue.length === 0) {
    return;
  }

  isProcessingCartAttribute = true;

  // Snapshot current batch so each enqueued promise can be resolved/rejected.
  const batch = [];
  const attributes = {};
  while (cartAttributeQueue.length > 0) {
    const item = cartAttributeQueue.shift();
    batch.push(item);
    attributes[item.key] = item.value;
  }

  try {
    const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes }),
    });

    if (!response.ok) {
      throw response;
    }

    const data = await response.json();
    batch.forEach(({ resolve }) => resolve(data));
  } catch (error) {
    console.error('Error setting cart attributes:', error);
    batch.forEach(({ reject }) => reject(error));
  }

  setTimeout(() => {
    isProcessingCartAttribute = false;
    processCartAttributeQueue();
  }, currentDelay);
};

const setCartAttribute = async (key, value) => {
  if (typeof key !== 'string' || !key) {
    throw new Error('Cart attribute key must be a non-empty string');
  }

  return new Promise((resolve, reject) => {
    cartAttributeQueue.push({ key, value, resolve, reject });

    // Clear existing batch window and set a new one
    clearTimeout(batchTimeout);
    batchTimeout = setTimeout(() => {
      processCartAttributeQueue();
    }, BATCH_WINDOW);
  });
};

class UrlDiscountModule {
  constructor() {
    this.urlDiscountCookie = getCookieValue('discount_code');
    const urlParams = new URLSearchParams(window.location.search);
    this.urlDiscountValue = Number(urlParams.get('utm_term') || getCookieValue('url_discount_value'));
    this.existingCustomerCode = urlParams.get('existing_customer_code');
  }

  async init() {
    // Get Cart Data
    try {
      this.cart = await fetch(`${window.Shopify.routes.root}cart.json`).then((response) => response.json());

      if (this.cart && Array.isArray(this.cart.discount_codes)) {
        this.discountCodes = this.cart.discount_codes;
        this.hasCartItems = !!this.cart.items.length;
        this.discountAttribute = this.cart.attributes.discount_code;
      }
    } catch (error) {
      console.error('Error retrieving discount codes:', error);
    }

    if (this.existingCustomerCode) {
      this.handleExistingCustomerDiscount();
    }

    this.handleDiscount();
  }

  async handleDiscount() {
    if (!this.urlDiscountCookie && !window.snocks.cart.attributes.discount_code) return;

    if (!this.discountCodes.length || !this.urlDiscountCookie || !this.urlDiscountValue) {
      await this.clearDiscountFromAttributes();
      return;
    }

    setCookieValue('url_discount_value', this.urlDiscountValue, 1);

    this.localCode = { code: this.urlDiscountCookie.toUpperCase(), value: this.urlDiscountValue };

    this.isCodeAlreadyApplied = this.discountCodes?.some(
      (discount) => discount.code.toUpperCase() === this.localCode.code,
    );

    this.isDiscountApplicable = this.discountCodes?.some(
      (discount) => discount.code.toUpperCase() === this.localCode.code && discount.applicable,
    );

    if (
      (this.isCodeAlreadyApplied && this.isDiscountApplicable && this.discountAttribute === this.localCode.code) ||
      (!this.isCodeAlreadyApplied && this.hasCartItems && !this.isDiscountApplicable)
    )
      return;

    if (this.isCodeAlreadyApplied && this.hasCartItems && !this.isDiscountApplicable) {
      await this.clearDiscountFromAttributes();
      return;
    }

    if (!this.isCodeAlreadyApplied || (this.isCodeAlreadyApplied && this.discountAttribute !== this.localCode.code)) {
      await this.addDiscountToAttributes();
    }
  }

  async clearDiscountFromAttributes() {
    if (!this.discountAttribute) return;
    try {
      setCartAttribute('discount_code', '');
      setCartAttribute('dh-new-customer-code', '');
      setCartAttribute('dh-existing-customer-code', '');
      setCookieValue('url_discount_value', '', 1);
    } catch (error) {
      console.error('Error clearing discount from cart attributes:', error);
    }
  }

  async addDiscountToAttributes() {
    const discountValue =
      typeof this.localCode.value === 'number' && !Number.isNaN(this.localCode.value) ? this.localCode.value / 100 : '';

    try {
      setCartAttribute('discount_code', `${this.localCode.code}:${discountValue}`);
    } catch (error) {
      console.error('Error adding discount to cart attributes:', error);
    }
  }

  handleExistingCustomerDiscount() {
    try {
      setCartAttribute('dh-new-customer-code', this.urlDiscountCookie);
      setCartAttribute('dh-existing-customer-code', this.existingCustomerCode);
    } catch (error) {
      console.error('Error adding existing customer discount to cart attributes:', error);
    }
  }
}

const urlDiscountInstance = new UrlDiscountModule();

if (document.readyState !== 'complete') {
  document.addEventListener('DOMContentLoaded', async () => {
    urlDiscountInstance.init();
  });
} else {
  urlDiscountInstance.init();
}
